import React, { useMemo } from "react";
import { Link } from "wouter";
import { useQuery } from "urql";
import { useQueryState } from "../../../components/src/use-query-state";
import { sortBy } from "lodash";

import { DateHelper } from "../../../common/src";
import { DateForm } from "../../../components/src/dateForm";
import { formatNumberWithSpaces } from "../../../common/src/utils/FormatFunctions";
import { ReactTable } from "../../../components/src/react-table/ReactTable";
import { SelectColumnFilter } from "../../../components/src/react-table/ReactTableFilters";

import { BANK_ACCOUNT_TRANSACTIONS_QUERY } from "../../../graphql/src/bankaccounttransaction/queries";
import { BankAccountTransaction } from "../../../graphql/src/types";

export function BankAccountTransactionsPage(): React.ReactElement {
    const [startDate] = useQueryState("startDate", DateHelper.addMonths(new Date(), -1).toISOString().slice(0, 10));
    const [{ fetching, error, data }] = useQuery<{ bankAccountTransactions?: Partial<BankAccountTransaction>[] }>({
        query: BANK_ACCOUNT_TRANSACTIONS_QUERY,
        requestPolicy: "cache-and-network",
        variables: { filter: { startDate } }
    });

    const columns = useMemo(
        () => [
            {
                accessorKey: "_id",
                header: "Id",
                size: 90,
                cell: (cellProps) => {
                    return <Link to={`/bankaccounttransactions/${cellProps.getValue()}`}>{cellProps.getValue()}</Link>;
                }
            },
            {
                accessorKey: "externalId",
                header: "External id",
                size: 90
            },
            {
                id: "client.name",
                accessorKey: "client.name",
                header: "Client",
                filter: SelectColumnFilter,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.client) {
                        return <Link to={`/parties/${row.original.client._id}`}>{cellProps.getValue()}</Link>;
                    } else {
                        return null;
                    }
                },
                size: 75
            },
            {
                accessorKey: "instrument.iban",
                header: "IBAN",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.client) {
                        return (
                            <Link to={`/parties/${row.original.client._id}/instruments/${row.original.instrument._id}`}>
                                {cellProps.getValue()}
                            </Link>
                        );
                    } else {
                        return null;
                    }
                },
                filter: SelectColumnFilter,
                size: 90
            },
            {
                accessorKey: "date",
                header: "Date"
            },
            {
                accessorKey: "instrument.currency",
                header: "Currency",
                filter: SelectColumnFilter,
                size: 30
            },
            {
                accessorKey: "amount",
                header: "Amount",
                cell: (cellProps) => {
                    return <div style={{ textAlign: "right" }}>{formatNumberWithSpaces(cellProps.getValue())}</div>;
                }
            },
            {
                accessorKey: "status",
                header: "Status",
                filter: SelectColumnFilter
            },
            {
                accessorKey: "reference",
                header: "Reference",
                size: 150
            },
            {
                accessorKey: "description",
                header: "Description",
                size: 120
            },
            {
                accessorKey: "createTimestamp",
                header: "Create timestamp",
                size: 80
            },
            {
                accessorKey: "updateTimestamp",
                header: "Update timestamp",
                size: 80
            },
            {
                accessorKey: "updateUserInfo.name",
                header: "Update user",
                size: 100
            }
        ],
        []
    );

    const sortedTransactions = useMemo(() => {
        if (!data?.bankAccountTransactions) return [];
        return sortBy(data.bankAccountTransactions, "date").reverse();
    }, [data?.bankAccountTransactions]);

    if (fetching)
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );

    if (error)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    if (!data) return <div></div>;

    return (
        <div>
            <div className="flex">
                <DateForm className="float-right" defaultDateString={startDate} dateName={"startDate"}></DateForm>
            </div>
            <p>Number of transactions: {sortedTransactions.length}</p>

            <ReactTable columns={columns} data={sortedTransactions} defaultHiddenColumns={["updateTimestamp", "status"]} />
        </div>
    );
}
